import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import {useEffect, useMemo, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AddApartmentModal from 'src/components/Modals/AddApartment';
import ConfirmDeleteModal from 'src/components/Modals/ConfirmDeleteModal/ConfirmDeleteModal';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import {apartmentServiceDelete} from 'src/apis/autoGenerate/services';
import {getApartmentList, selectProperty} from 'src/redux/slice/propertySlice';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {selectDevice, getDeviceList} from 'src/redux/slice/deviceSlice';
import {fetchAllPropertyData} from 'src/redux/slice/propertyCacheSlice';
import {Apartment} from 'src/apis/autoGenerate/types';
import message from 'src/components/Message';
import Spin from 'src/components/Spin';
import SearchApartments from 'src/components/SearchApartment';
import {GoogleMapContext} from 'src/components/Modals/AddApartment/context';
import GoogleMap from 'google-map-react';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {useCanEdit} from 'src/hooks';
import {formatDate} from 'src/utils/common';
import PropertyTypeSelectionModal from 'src/components/Modals/PropertyTypeSelection';
import {PropertyType} from 'src/apis/autoGenerate/types/apartment';
import {RoomStatusEnum} from 'src/apis/autoGenerate/types/room';
import AddCommercialPropertyModal from 'src/components/Modals/AddCommercialProperty';
import LicenseQuotaDisplay from 'src/components/LicenseQuotaDisplay';
import {useLicenseQuota} from 'src/hooks/useLicenseQuota';
import {PageHeader, EmptyState} from 'src/components/ui';
import {neutral, brand, semantic} from 'src/theme';

type Order = 'asc' | 'desc';
type OrderByField = 'name' | 'street' | 'createdAt' | 'propertyType';
type TypeFilter = 'all' | PropertyType;

const getPropertyIcon = (propertyType?: PropertyType) => {
  const isCommercial = propertyType === PropertyType.COMMERCIAL;
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: isCommercial ? semantic.warning.light : brand[100],
        flexShrink: 0,
      }}
    >
      {isCommercial ? (
        <BusinessRoundedIcon sx={{color: semantic.warning.main, fontSize: 28}} />
      ) : (
        <HomeRoundedIcon sx={{color: brand[500], fontSize: 28}} />
      )}
    </Box>
  );
};

const getPropertyTypeBadge = (propertyType?: PropertyType) => {
  if (propertyType === PropertyType.COMMERCIAL) {
    return (
      <Chip
        label="Commercial"
        size="small"
        sx={{
          bgcolor: semantic.warning.light,
          color: semantic.warning.dark,
          fontWeight: 600,
          fontSize: '0.75rem',
          height: 24,
          borderRadius: 1,
        }}
      />
    );
  }
  return (
    <Chip
      label="Multi-Family"
      size="small"
      sx={{
        bgcolor: brand[50],
        color: brand[500],
        fontWeight: 600,
        fontSize: '0.75rem',
        height: 24,
        borderRadius: 1,
      }}
    />
  );
};

export default function Apartments() {
  const [geocode, setGeocode] = useState<any>(null);
  const [places, setPlaces] = useState<any>(null);

  const [addApartment, setAddApartment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [editApartmentInfo, setEditApartmentInfo] = useState<Apartment | null>();
  const [searchApartment, setSearchApartment] = useState<Apartment | null>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const property = useAppSelector(selectProperty);
  const account = useAppSelector(selectAccount);
  const device = useAppSelector(selectDevice);
  const propertyCache = useAppSelector(state => state.propertyCache);
  const canEdit = useCanEdit();

  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<OrderByField>('createdAt');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [showPropertyTypeSelection, setShowPropertyTypeSelection] = useState(false);
  const [selectedPropertyType, setSelectedPropertyType] = useState<PropertyType | null>(null);
  const [showCommercialModal, setShowCommercialModal] = useState(false);
  const apartmentFetchContextRef = useRef<string | null>(null);

  const unitStatsByApartment = useMemo(() => {
    const stats: {[id: string]: {total: number; occupied: number; vacant: number}} = {};
    for (const [aptId, buildings] of Object.entries(propertyCache.buildings)) {
      let total = 0;
      let occupied = 0;
      let vacant = 0;
      for (const building of buildings) {
        if (!building.id || !propertyCache.rooms[building.id]) continue;
        for (const room of propertyCache.rooms[building.id]) {
          total += 1;
          if (room.status === RoomStatusEnum.Occupied) {
            occupied += 1;
          } else {
            // Treat vacant + unknown as vacant for list summary (matches dashboard)
            vacant += 1;
          }
        }
      }
      stats[aptId] = {total, occupied, vacant};
    }
    return stats;
  }, [propertyCache.buildings, propertyCache.rooms]);

  const deviceCountByApartment = useMemo(() => {
    const counts: {[id: string]: number} = {};
    for (const d of device.deviceList) {
      if (d.apartmentId) {
        counts[d.apartmentId] = (counts[d.apartmentId] || 0) + 1;
      }
    }
    return counts;
  }, [device.deviceList]);

  const summaryStats = useMemo(() => {
    const list = property.apartmentList ?? [];
    const total = list.length;
    const multiFamily = list.filter(a => a.propertyType === PropertyType.MULTI_FAMILY).length;
    const commercial = list.filter(a => a.propertyType === PropertyType.COMMERCIAL).length;
    const totalUnits = list.reduce(
      (sum, a) => sum + (a.id ? unitStatsByApartment[a.id]?.total || 0 : 0),
      0
    );
    return {total, multiFamily, commercial, totalUnits};
  }, [property.apartmentList, unitStatsByApartment]);

  const filteredAndSortedApartments = useMemo(() => {
    let list = searchApartment
      ? (property.apartmentList ?? []).filter(_ => _.id === searchApartment.id)
      : property.apartmentList ?? [];

    if (typeFilter !== 'all') {
      list = list.filter(a => a.propertyType === typeFilter);
    }

    return [...list].sort((a, b) => {
      if (orderBy === 'createdAt') {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      if (orderBy === 'propertyType') {
        const typeA = a.propertyType || '';
        const typeB = b.propertyType || '';
        return order === 'asc' ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
      }
      const valA = a[orderBy as keyof typeof a]?.toString() || '';
      const valB = b[orderBy as keyof typeof b]?.toString() || '';
      return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  }, [property.apartmentList, searchApartment, typeFilter, order, orderBy]);

  useEffect(() => {
    if (property.apartmentList.length === 0) {
      setEditApartmentInfo(null);
    }
  }, [property.apartmentList]);

  const orgId = account.role?.org_id || account.organization?.id;
  const userId = account.userId;
  const apartmentFetchContext = `${orgId || ''}:${userId || ''}`;

  useEffect(() => {
    if (!orgId && !userId) return;

    if (
      property.apartmentList.length === 0 &&
      !property.loadingList &&
      apartmentFetchContextRef.current !== apartmentFetchContext
    ) {
      apartmentFetchContextRef.current = apartmentFetchContext;
      getApartmentList(orgId, userId).catch(error => {
        console.error('Error fetching apartments:', error);
      });
    }

    if (orgId && !propertyCache.lastFetched && !propertyCache.loading) {
      dispatch(fetchAllPropertyData({orgId, userId}));
    }
    if (orgId && device.deviceList.length === 0 && !device.loadingList) {
      getDeviceList(orgId, userId);
    }
  }, [
    orgId,
    userId,
    apartmentFetchContext,
    property.apartmentList.length,
    property.loadingList,
    dispatch,
    propertyCache.lastFetched,
    propertyCache.loading,
    device.deviceList.length,
    device.loadingList,
  ]);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? 'action-popover' : undefined;

  const handleConfirm = () => {
    setAddApartment(false);
    setSelectedPropertyType(null);
    setLoading(true);
    getApartmentList(account.role?.org_id, account.userId, true)
      .then(() => {
        message.success('Property list updated');
      })
      .catch(error => {
        console.error('Error refreshing property list:', error);
        message.error('Failed to refresh property list');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleNavigateToProperty = (apartment: Apartment) => {
    if (apartment.propertyType === PropertyType.COMMERCIAL) {
      navigate(`/property/commercialDetail/${apartment.id}`);
    } else {
      navigate(`/property/apartmentDetail/${apartment.id}`);
    }
  };

  const handleManageProperty = () => {
    setAnchorEl(null);
    if (editApartmentInfo) {
      handleNavigateToProperty(editApartmentInfo);
    }
  };

  const handleSettingsProperty = () => {
    setAnchorEl(null);
    if (editApartmentInfo?.propertyType === PropertyType.COMMERCIAL) {
      setSelectedPropertyType(PropertyType.COMMERCIAL);
      setShowCommercialModal(true);
    } else {
      setSelectedPropertyType(PropertyType.MULTI_FAMILY);
      setAddApartment(true);
    }
  };

  const handleRemoveApartment = () => {
    setAnchorEl(null);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (editApartmentInfo) {
      setDeleteLoading(true);
      apartmentServiceDelete(editApartmentInfo.id as string)
        .then(() => {
          message.success('Delete successfully');
          return getApartmentList(account.role?.org_id, account.userId, true);
        })
        .catch(err => {
          message.error('Failed to delete: ' + err.message);
        })
        .finally(() => {
          setDeleteLoading(false);
          setShowDeleteConfirm(false);
          setEditApartmentInfo(null);
        });
    }
  };

  const handleCloseDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setEditApartmentInfo(null);
  };

  const handleSelectApartment = (val: Apartment | null) => {
    setSearchApartment(val ?? undefined);
  };

  const handlePropertyTypeSelect = (propertyType: PropertyType) => {
    setSelectedPropertyType(propertyType);
    setShowPropertyTypeSelection(false);
    if (propertyType === PropertyType.MULTI_FAMILY) {
      setAddApartment(true);
    } else if (propertyType === PropertyType.COMMERCIAL) {
      setShowCommercialModal(true);
    }
  };

  const handleCommercialConfirm = () => {
    setShowCommercialModal(false);
    setSelectedPropertyType(null);
    setLoading(true);
    getApartmentList(account.role?.org_id, account.userId, true)
      .then(() => {
        message.success('Property list updated');
      })
      .catch(error => {
        console.error('Error refreshing property list:', error);
        message.error('Failed to refresh property list');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  function handleGoogleLoaded(maps: any) {
    setGeocode(new maps.Geocoder());
    setPlaces(maps.places);
  }

  const handleSortChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    if (value === 'name-asc') {
      setOrder('asc');
      setOrderBy('name');
    } else if (value === 'name-desc') {
      setOrder('desc');
      setOrderBy('name');
    } else if (value === 'date-desc') {
      setOrder('desc');
      setOrderBy('createdAt');
    } else if (value === 'date-asc') {
      setOrder('asc');
      setOrderBy('createdAt');
    } else if (value === 'type') {
      setOrder('asc');
      setOrderBy('propertyType');
    }
  };

  const getSortValue = () => {
    if (orderBy === 'name') return order === 'asc' ? 'name-asc' : 'name-desc';
    if (orderBy === 'createdAt') return order === 'desc' ? 'date-desc' : 'date-asc';
    if (orderBy === 'propertyType') return 'type';
    return 'date-desc';
  };

  const {isAtLimit} = useLicenseQuota();

  const getAddress = (apartment: Apartment) => {
    return [apartment.street, apartment.city, apartment.state, apartment.zipCode]
      .filter(Boolean)
      .join(', ');
  };

  const getPropertyStats = (apartment: Apartment) => {
    const unitStats = apartment.id
      ? unitStatsByApartment[apartment.id] || {total: 0, occupied: 0, vacant: 0}
      : {total: 0, occupied: 0, vacant: 0};
    const deviceCount = apartment.id ? deviceCountByApartment[apartment.id] || 0 : 0;
    return {
      unitsLabel: `${unitStats.total} ${unitStats.total === 1 ? 'Unit' : 'Units'}`,
      occupancyLabel:
        unitStats.total > 0
          ? `${unitStats.occupied} Occupied · ${unitStats.vacant} Vacant`
          : '',
      devicesLabel: deviceCount > 0 ? `${deviceCount} ${deviceCount === 1 ? 'Device' : 'Devices'}` : '',
    };
  };

  return (
    <>
      <GoogleMapContext.Provider value={{geocode, places}}>
        <Box>
          <Spin spinning={property.loadingList || loading} />
          {(property.apartmentList ?? []).length === 0 && !property.loadingList ? (
            <Grid
              container
              direction="column"
              alignItems="center"
              justifyContent="center"
              height="calc(100vh - 80px)"
            >
              <EmptyState
                title="No properties yet"
                description="Add your first property to start managing your community."
                action={
                  canEdit
                    ? {
                        label: 'Add Property',
                        onClick: () => setShowPropertyTypeSelection(true),
                        icon: <AddIcon />,
                      }
                    : undefined
                }
              />
            </Grid>
          ) : (
            <>
              <PageHeader
                title="Properties"
                actions={<LicenseQuotaDisplay variant="header-card" />}
              />

              {/* Summary Stats Bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 3,
                  gap: 1.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{color: '#374151', fontWeight: 600, fontSize: '0.875rem'}}
                >
                  {summaryStats.total} Properties
                </Typography>
                <Typography variant="body2" sx={{color: '#D1D5DB'}}>
                  ·
                </Typography>
                <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.875rem'}}>
                  {summaryStats.multiFamily} Multi-Family
                </Typography>
                <Typography variant="body2" sx={{color: '#D1D5DB'}}>
                  ·
                </Typography>
                <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.875rem'}}>
                  {summaryStats.commercial} Commercial
                </Typography>
                {summaryStats.totalUnits > 0 && (
                  <>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, ml: 2}}>
                      <KeyRoundedIcon sx={{color: '#9CA3AF', fontSize: 16}} />
                      <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.875rem'}}>
                        {summaryStats.totalUnits} Units Total
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>

              {/* Search / Filter / Sort Bar */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                  gap: 2,
                }}
              >
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
                  <Box sx={{minWidth: 250}}>
                    <SearchApartments onChange={handleSelectApartment} />
                  </Box>
                  <FormControl size="small" sx={{minWidth: 120}}>
                    <Select
                      value={typeFilter}
                      onChange={e => setTypeFilter(e.target.value as TypeFilter)}
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'white',
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {py: 1},
                      }}
                    >
                      <MenuItem value="all">Type: All</MenuItem>
                      <MenuItem value={PropertyType.MULTI_FAMILY}>Multi-Family</MenuItem>
                      <MenuItem value={PropertyType.COMMERCIAL}>Commercial</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{minWidth: 100}}>
                    <Select
                      value={getSortValue()}
                      onChange={handleSortChange}
                      displayEmpty
                      sx={{
                        borderRadius: 2,
                        bgcolor: 'white',
                        fontSize: '0.875rem',
                        '& .MuiSelect-select': {py: 1},
                      }}
                    >
                      <MenuItem value="date-desc">Newest</MenuItem>
                      <MenuItem value="date-asc">Oldest</MenuItem>
                      <MenuItem value="name-asc">Name A-Z</MenuItem>
                      <MenuItem value="name-desc">Name Z-A</MenuItem>
                      <MenuItem value="type">Type</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                {canEdit && (
                  <Button
                    startIcon={<AddIcon />}
                    variant="contained"
                    onClick={() => {
                      setEditApartmentInfo(null);
                      setShowPropertyTypeSelection(true);
                    }}
                    sx={{
                      borderRadius: 6,
                      height: 40,
                      px: 3,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: 'none',
                      '&:hover': {boxShadow: 'none'},
                    }}
                  >
                    Property
                  </Button>
                )}
              </Box>

              {/* Property Cards */}
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0}}>
                {filteredAndSortedApartments.map((apartment, index) => {
                  const stats = getPropertyStats(apartment);
                  return (
                  <Box key={apartment.id}>
                    {index > 0 && <Divider />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 3,
                        py: 2.5,
                        px: 1,
                        cursor: 'pointer',
                        borderRadius: 2,
                        transition: 'background-color 0.15s',
                        '&:hover': {bgcolor: '#F9FAFB'},
                      }}
                      onClick={() => handleNavigateToProperty(apartment)}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          alignItems: 'center',
                          flex: '1 1 220px',
                          minWidth: 200,
                        }}
                      >
                        {getPropertyIcon(apartment.propertyType)}
                        <Box sx={{minWidth: 0}}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: neutral[900],
                              fontSize: '1rem',
                              lineHeight: 1.3,
                            }}
                          >
                            {apartment.name}
                          </Typography>
                          <Box sx={{mt: 0.75}}>{getPropertyTypeBadge(apartment.propertyType)}</Box>
                        </Box>
                      </Box>

                      <Box sx={{flex: '1.4 1 200px', minWidth: 160}}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9CA3AF',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Address
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6B7280',
                            fontSize: '0.875rem',
                            mt: 0.25,
                          }}
                        >
                          {getAddress(apartment) || '—'}
                        </Typography>
                      </Box>

                      <Box sx={{flex: '0 1 140px', minWidth: 100}}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9CA3AF',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Units
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: neutral[800],
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            mt: 0.25,
                          }}
                        >
                          {stats.unitsLabel}
                        </Typography>
                        {stats.occupancyLabel && (
                          <Typography variant="body2" sx={{color: '#6B7280', fontSize: '0.75rem'}}>
                            {stats.occupancyLabel}
                          </Typography>
                        )}
                        {stats.devicesLabel && (
                          <Typography variant="body2" sx={{color: '#9CA3AF', fontSize: '0.75rem'}}>
                            {stats.devicesLabel}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{flex: '0 0 150px'}}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9CA3AF',
                            fontWeight: 600,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                          }}
                        >
                          Created
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#6B7280',
                            fontSize: '0.813rem',
                            mt: 0.25,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {apartment.createdAt ? formatDate(apartment.createdAt) : '—'}
                        </Typography>
                      </Box>

                      {canEdit && (
                        <IconButton
                          size="small"
                          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                            event.stopPropagation();
                            setAnchorEl(event.currentTarget);
                            setEditApartmentInfo(apartment);
                          }}
                          sx={{
                            border: '1px solid #E5E7EB',
                            borderRadius: 2,
                            p: 0.75,
                            flexShrink: 0,
                            '&:hover': {bgcolor: '#F3F4F6'},
                          }}
                        >
                          <MoreHorizIcon sx={{fontSize: 18, color: '#6B7280'}} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  );
                })}

                {filteredAndSortedApartments.length === 0 && !property.loadingList && (
                  <Box sx={{textAlign: 'center', py: 8}}>
                    <Typography variant="body1" sx={{color: '#9CA3AF'}}>
                      No properties match your filters
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Property Type Selection Modal */}
        <PropertyTypeSelectionModal
          open={showPropertyTypeSelection}
          onClose={() => setShowPropertyTypeSelection(false)}
          onSelectPropertyType={handlePropertyTypeSelect}
        />

        {/* Add Apartment Modal (Multi-Family) */}
        {addApartment && selectedPropertyType === PropertyType.MULTI_FAMILY && (
          <AddApartmentModal
            open={addApartment}
            setOpen={setAddApartment}
            handleConfirm={handleConfirm}
            initData={editApartmentInfo}
            propertyType={selectedPropertyType}
          />
        )}

        {/* Commercial Property Modal */}
        {showCommercialModal && (
          <AddCommercialPropertyModal
            open={showCommercialModal}
            setOpen={setShowCommercialModal}
            handleConfirm={handleCommercialConfirm}
            initData={editApartmentInfo}
          />
        )}

        {/* Actions Popover */}
        <Popover
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
          slotProps={{
            paper: {
              sx: {
                borderRadius: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                minWidth: 160,
                mt: 0.5,
              },
            },
          }}
        >
          <List disablePadding sx={{py: 0.5}}>
            <ListItem disablePadding>
              <ListItemButton onClick={handleManageProperty} sx={{py: 1, px: 2}}>
                <ListItemIcon sx={{minWidth: 32}}>
                  <ManageAccountsOutlinedIcon sx={{fontSize: 20, color: '#6B7280'}} />
                </ListItemIcon>
                <ListItemText
                  primary="Manage"
                  primaryTypographyProps={{fontSize: '0.875rem', color: '#374151'}}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleSettingsProperty} sx={{py: 1, px: 2}}>
                <ListItemIcon sx={{minWidth: 32}}>
                  <SettingsOutlinedIcon sx={{fontSize: 20, color: '#6B7280'}} />
                </ListItemIcon>
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{fontSize: '0.875rem', color: '#374151'}}
                />
              </ListItemButton>
            </ListItem>
            {canEdit && (
              <>
                <Divider sx={{my: 0.5}} />
                <ListItem disablePadding>
                  <ListItemButton onClick={handleRemoveApartment} sx={{py: 1, px: 2}}>
                    <ListItemText
                      primary="Delete"
                      primaryTypographyProps={{fontSize: '0.875rem', color: '#DC2626'}}
                    />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </List>
        </Popover>

        <ConfirmDeleteModal
          open={showDeleteConfirm}
          onClose={handleCloseDeleteConfirm}
          onConfirm={handleConfirmDelete}
          title="Delete Property"
          itemName={editApartmentInfo?.name || ''}
          itemType="Property"
          loading={deleteLoading}
          warningText="Warning: Deleting a property will also delete all related buildings, residents, devices, guests, and deliveries. This action is irreversible. Are you sure you want to continue?"
        />

        {/* Hidden Google Map for geocoding in modals */}
        <div style={{position: 'absolute', width: 0, height: 0, overflow: 'hidden'}}>
          <GoogleMap
            bootstrapURLKeys={{
              key: (process.env.REACT_APP_GOOGLE_MAP_KEY ||
                'AIzaSyAoEp7c67IE0ZcQHy173NBusAG0hOGMcuQ') as string,
              libraries: ['places'],
            }}
            defaultCenter={{lat: 39, lng: -96}}
            defaultZoom={3.7}
            onGoogleApiLoaded={({maps}) => handleGoogleLoaded(maps)}
            yesIWantToUseGoogleMapApiInternals
          />
        </div>
      </GoogleMapContext.Provider>
    </>
  );
}
